// Iter 82.10 / Phase E3.1 Sprint 3 — Payslip Print Route.
//
// Hidden, server-render-friendly page targeted by the Playwright
// PDF pipeline (`/payslip/:id/print`). The page reads the snapshotted
// payslip + line server-side, lays out the canonical Indian payslip
// format, and stamps a `data-testid="payslip-cover"` selector that
// `pdf_render` waits on before printing.
//
// Layout: Header + Employer block + Employee block + Working Days +
// Earnings + Deductions + Employer Contributions + Net Pay (figures
// + words) + TDS computation + Footer with SHA-256 placeholder.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Indian numbering convention number-to-words (basic, for payslip use).
const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
function numberToWords(n) {
    if (n === 0) return "Zero";
    const abs = Math.floor(Math.abs(n));
    function under1000(num) {
        if (num < 20) return ones[num];
        if (num < 100) return `${tens[Math.floor(num / 10)]}${num % 10 ? " " + ones[num % 10] : ""}`;
        return `${ones[Math.floor(num / 100)]} Hundred${num % 100 ? " " + under1000(num % 100) : ""}`;
    }
    function indian(num) {
        if (num < 1000) return under1000(num);
        if (num < 100000) return `${under1000(Math.floor(num / 1000))} Thousand${num % 1000 ? " " + under1000(num % 1000) : ""}`;
        if (num < 10000000) {
            const lakh = Math.floor(num / 100000);
            return `${under1000(lakh)} Lakh${num % 100000 ? " " + indian(num % 100000) : ""}`;
        }
        const crore = Math.floor(num / 10000000);
        return `${indian(crore)} Crore${num % 10000000 ? " " + indian(num % 10000000) : ""}`;
    }
    const sign = n < 0 ? "Minus " : "";
    return `${sign}${indian(abs)} Rupees Only`;
}

function mask(s, keep = 4) {
    if (!s) return "—";
    const str = String(s);
    if (str.length <= keep) return str;
    return "x".repeat(str.length - keep) + str.slice(-keep);
}

function Row({ label, value, mono }) {
    return (
        <tr>
            <td style={{ padding: "4px 8px", color: "#475569", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</td>
            <td style={{ padding: "4px 8px", fontFamily: mono ? "'JetBrains Mono', monospace" : undefined, fontSize: 12 }}>{value || "—"}</td>
        </tr>
    );
}

export default function PayslipPrint() {
    const { pid } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/payroll/payslip/${pid}`);
                const ps = res.data.payslip;
                const lineRes = await api.get(`/payroll/run/${ps.run_id}/lines/${ps.employee_id}`);
                setData({ payslip: ps, line: lineRes.data });
            } catch (err) {
                setError(err?.response?.data?.detail || err?.message || "Failed to load");
            }
        })();
    }, [pid]);

    if (error) return <div style={{ padding: 40 }} data-testid="payslip-error">Error: {error}</div>;
    if (!data) return <div style={{ padding: 40 }} data-testid="payslip-loading">Loading payslip…</div>;

    const ps = data.payslip;
    const line = data.line;
    const emp = (line?.snapshots || {}).employee || {};
    const struct = (line?.snapshots || {}).structure || {};
    const att = line?.attendance_summary || {};
    const tds = line?.tds_block || {};

    return (
        <div
            style={{
                width: "210mm",
                minHeight: "297mm",
                padding: "12mm 14mm",
                color: "#0f172a",
                fontFamily: "'Inter', system-ui, sans-serif",
                background: "#fff",
                lineHeight: 1.4,
            }}
            data-testid="payslip-cover"
        >
            {/* Header */}
            <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #cbd5e1", paddingBottom: 10, marginBottom: 14 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 18, letterSpacing: "0.02em" }}>REEDOS</h1>
                    <p style={{ margin: "4px 0 0", fontSize: 11, color: "#64748b" }}>Payslip · {ps.period_month_snapshot}</p>
                </div>
                <div style={{ textAlign: "right", fontSize: 11, color: "#64748b" }}>
                    <div>Payslip ID: <span style={{ fontFamily: "monospace" }}>{ps.id}</span></div>
                    <div>Generated: {ps.generated_at?.slice(0, 19).replace("T", " ")}</div>
                    <div>SHA-256: <span style={{ fontFamily: "monospace" }}>{(ps.sha256 || "").slice(0, 16)}…</span></div>
                </div>
            </header>

            {/* Employee block */}
            <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 14 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                        <Row label="Employee Code" value={emp.employee_code} mono />
                        <Row label="Name" value={emp.full_name} />
                        <Row label="Department" value={emp.department} />
                        <Row label="Designation" value={emp.designation} />
                        <Row label="Date of Joining" value={emp.date_of_joining} mono />
                        {emp.last_working_day && <Row label="Last Working Day" value={emp.last_working_day} mono />}
                    </tbody>
                </table>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                        <Row label="PAN" value={emp.pan} mono />
                        <Row label="Aadhaar (last 4)" value={emp.aadhaar_last4 || "—"} mono />
                        <Row label="Work State" value={emp.work_state} />
                        <Row label="Tax Regime" value={(emp.tax_regime || "new").toUpperCase()} />
                        <Row label="Bank A/c" value={mask(emp.bank?.account_number)} mono />
                        <Row label="IFSC" value={emp.bank?.ifsc} mono />
                    </tbody>
                </table>
            </section>

            {/* Working days block */}
            <section style={{ background: "#f8fafc", padding: 10, borderRadius: 4, marginBottom: 14, fontSize: 11 }}>
                <strong>Working Days:</strong>
                <span style={{ marginLeft: 12 }}>Days in Month: <strong>{att.month_days || 0}</strong></span>
                <span style={{ marginLeft: 12 }}>Paid: <strong>{att.paid_days || 0}</strong></span>
                <span style={{ marginLeft: 12 }}>LOP: <strong>{att.lop_days || 0}</strong></span>
                <span style={{ marginLeft: 12 }}>Holidays + WO: <strong>{(att.holidays || 0) + (att.weekly_offs || 0)}</strong></span>
                <span style={{ marginLeft: 12 }}>Leave (Paid): <strong>{att.leave_days_paid || 0}</strong></span>
                <span style={{ marginLeft: 12 }}>Pro-rata: <strong>{((line.pro_rata_fraction || 0) * 100).toFixed(2)}%</strong></span>
            </section>

            {/* Earnings + Deductions side-by-side */}
            <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
                <div>
                    <h2 style={{ fontSize: 13, margin: "0 0 6px", color: "#0f172a" }}>Earnings</h2>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <thead><tr style={{ background: "#f1f5f9" }}><th style={{ padding: 6, textAlign: "left" }}>Component</th><th style={{ padding: 6, textAlign: "right" }}>Amount</th></tr></thead>
                        <tbody>
                            {(line.earnings || []).map((r) => (
                                <tr key={r.component_code}><td style={{ padding: 6 }}>{r.name}</td><td style={{ padding: 6, textAlign: "right", fontFamily: "monospace" }}>{inr(r.amount)}</td></tr>
                            ))}
                            {(line.reimbursements || []).map((r, i) => (
                                <tr key={`reim${i}`}><td style={{ padding: 6 }}>{r.label}</td><td style={{ padding: 6, textAlign: "right", fontFamily: "monospace" }}>{inr(r.amount)}</td></tr>
                            ))}
                            {(line.variable_inputs || []).map((r, i) => (
                                <tr key={`vi${i}`}><td style={{ padding: 6 }}>{r.label}</td><td style={{ padding: 6, textAlign: "right", fontFamily: "monospace" }}>{inr(r.amount)}</td></tr>
                            ))}
                        </tbody>
                        <tfoot><tr style={{ borderTop: "1px solid #cbd5e1" }}><td style={{ padding: 6, fontWeight: 600 }}>Gross Earnings</td><td style={{ padding: 6, textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{inr(line.gross_earnings)}</td></tr></tfoot>
                    </table>
                </div>
                <div>
                    <h2 style={{ fontSize: 13, margin: "0 0 6px", color: "#0f172a" }}>Deductions</h2>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <thead><tr style={{ background: "#f1f5f9" }}><th style={{ padding: 6, textAlign: "left" }}>Component</th><th style={{ padding: 6, textAlign: "right" }}>Amount</th></tr></thead>
                        <tbody>
                            {(line.deductions || []).map((r) => (
                                <tr key={r.component_code}><td style={{ padding: 6 }}>{r.name}</td><td style={{ padding: 6, textAlign: "right", fontFamily: "monospace" }}>{inr(r.amount)}</td></tr>
                            ))}
                        </tbody>
                        <tfoot><tr style={{ borderTop: "1px solid #cbd5e1" }}><td style={{ padding: 6, fontWeight: 600 }}>Gross Deductions</td><td style={{ padding: 6, textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{inr(line.gross_deductions)}</td></tr></tfoot>
                    </table>
                </div>
            </section>

            {/* Net Pay block */}
            <section style={{ border: "2px solid #0f172a", padding: 10, borderRadius: 4, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>Net Pay (₹1 rounded)</div>
                    <div style={{ fontSize: 22, fontFamily: "monospace", fontWeight: 700 }}>{inr(line.net_pay)}</div>
                </div>
                <div style={{ flex: 1, marginLeft: 16, fontSize: 11, color: "#475569" }}>
                    <em>In Words:</em> <strong>{numberToWords(line.net_pay)}</strong>
                </div>
            </section>

            {/* Employer contribs + TDS */}
            <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
                <div>
                    <h2 style={{ fontSize: 13, margin: "0 0 6px" }}>Employer Contributions (Info Only)</h2>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <tbody>
                            {(line.employer_contributions || []).map((r) => (
                                <tr key={r.component_code}><td style={{ padding: 6 }}>{r.name}</td><td style={{ padding: 6, textAlign: "right", fontFamily: "monospace" }}>{inr(r.amount)}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div>
                    <h2 style={{ fontSize: 13, margin: "0 0 6px" }}>TDS Computation ({(tds.regime || "new").toUpperCase()} Regime)</h2>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                        <tbody>
                            <Row label="Projected Annual Income" value={inr(tds.projected_annual_income)} mono />
                            <Row label="Standard Deduction" value={inr(tds.standard_deduction_applied)} mono />
                            <Row label="Slab Tax" value={inr(tds.slab_tax)} mono />
                            <Row label="87A Rebate" value={inr(tds.rebate_87a)} mono />
                            <Row label="Cess (4%)" value={inr(tds.cess)} mono />
                            <Row label="Annual Tax" value={inr(tds.annual_tax)} mono />
                            <Row label="Monthly TDS (this run)" value={inr(tds.monthly_tds)} mono />
                        </tbody>
                    </table>
                </div>
            </section>

            <footer style={{ borderTop: "1px solid #cbd5e1", marginTop: 12, paddingTop: 8, fontSize: 10, color: "#64748b" }}>
                <p>This payslip is system-generated; no signature required.</p>
                <p>Document SHA-256: <span style={{ fontFamily: "monospace" }}>{ps.sha256}</span></p>
                <p>Password policy: <span style={{ fontFamily: "monospace" }}>{ps.password_policy_code}</span> · Structure: <span style={{ fontFamily: "monospace" }}>{struct.code} v{struct.version_no}</span></p>
            </footer>
        </div>
    );
}
